<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed, ref, watch } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { Attachment, Email } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { Separator } from '@ui/components/separator';
  import { toast } from '@ui/components/sonner';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  import AttachmentItem from '@/components/attachments/AttachmentItem.vue';
  import { shouldHideQuotedContent as getShouldHideQuotedContent } from '@/components/emails/emailQuoteVisibility';
  import { splitQuotedHistory } from '@/components/emails/quoteStrategies';
  import { detectLanguageAndDirection } from '@/components/emails/utils';
  import { downloadFilesAsZip } from '@/utils/zipDownload';

  interface Props {
    compact?: boolean;
    email: Email;
    emailEditorClass?: HTMLAttributes['class'];
    inlineAttachmentClass?: HTMLAttributes['class'];
    showSignature?: boolean;
    size?: '2xs' | 'xs' | 'sm';
  }

  const props = defineProps<Props>();

  defineOptions({ inheritAttrs: false });

  const iframeReady = ref(false);

  const textDirection = computed(() => detectLanguageAndDirection(props.email.body).direction);

  // Split off quoted history (client-agnostic — see quoteStrategies.ts) after
  // resolving inline cid: attachments.
  const processedBody = computed(() => {
    let body = props.email.body ?? '';
    props.email.attachments.forEach((attachment: Attachment) => {
      if (attachment.disposition === 'inline') {
        body = body.replace(`src="cid:${attachment.content_id}"`, `src="${attachment.src}"`);
      }
    });
    return splitQuotedHistory(body);
  });

  // Forward-detection runs on the VISIBLE body only. A "Forwarded message"
  // divider buried in the quoted history means a *prior* message was a forward,
  // not this one — so it shouldn't keep the quote expanded.
  const shouldHideQuotedContent = computed(() =>
    getShouldHideQuotedContent({
      body: processedBody.value.visibleHtml,
      showSignature: props.showSignature,
      subject: props.email.subject,
    }),
  );

  // User-controlled override for the trimmed thread history. Decoupled from
  // `showSignature` so expanding quoted content doesn't also reveal signatures.
  const showQuoted = ref(false);

  watch(
    () => props.email.id,
    () => {
      showQuoted.value = false;
    },
  );

  const effectiveHideQuoted = computed(() => shouldHideQuotedContent.value && !showQuoted.value);

  const hasQuotedContent = computed(() => processedBody.value.hasQuote);

  // Visible-only when collapsed; visible + Gmail-styled quote when expanded.
  const emailBody = computed<string>(() =>
    effectiveHideQuoted.value
      ? processedBody.value.visibleHtml
      : processedBody.value.styledFullHtml,
  );

  // Only offer the toggle when there's quoted content that would otherwise be hidden.
  const canToggleQuoted = computed(() => hasQuotedContent.value && shouldHideQuotedContent.value);

  const bodyIncludeStyle = computed(() => {
    return emailBody.value.includes('<style') && emailBody.value.includes('</style>');
  });

  const nonInlineAttachments = computed<Attachment[]>(() =>
    props.email.attachments.filter(
      (att) => att.disposition === 'attachment' || att.content_id === null,
    ),
  );

  const textClassName = computed(() => {
    if (props.size) return `text-${props.size}`;
    return 'text-xs';
  });

  const bodyPaddingClass = computed(() => (props.compact ? 'px-4' : 'px-6'));
  const quotedPaddingClass = computed(() => (props.compact ? 'px-3' : 'px-5'));

  const downloadableAttachments = computed(() =>
    nonInlineAttachments.value.filter((att): att is Attachment & { src: string } =>
      Boolean(att.src),
    ),
  );

  const isDownloadingAll = ref(false);

  const downloadAll = async () => {
    if (isDownloadingAll.value || !downloadableAttachments.value.length) return;

    isDownloadingAll.value = true;
    try {
      // Strip filesystem-unsafe chars from the subject for the archive name.
      const zipName = `${(props.email.subject || 'attachments').replace(/[/\\?%*:|"<>]/g, '-')}.zip`;
      await downloadFilesAsZip(
        downloadableAttachments.value.map((att) => ({ name: att.name, url: att.src })),
        zipName,
      );
    } catch {
      toast.error('Failed to download attachments');
    } finally {
      isDownloadingAll.value = false;
    }
  };

  const iframeEl = ref<HTMLIFrameElement | null>(null);

  const resizeIframeHeight = () => {
    const iframe = iframeEl.value;
    const doc = iframe?.contentWindow?.document;
    if (!iframe || !doc) return;
    // Collapse to 0 first so scrollHeight reflects the content, not the iframe's
    // current (possibly stale/larger) viewport height — otherwise it can't shrink.
    iframe.style.height = '0px';
    iframe.style.height = `${doc.documentElement.scrollHeight}px`;
  };

  const resizeIframe = (obj: Event) => {
    const iframe = obj.target as HTMLIFrameElement;

    if (!iframe.contentDocument) return;

    iframeEl.value = iframe;

    const fontsApiConnect = document.createElement('link');
    fontsApiConnect.rel = 'preconnect';
    fontsApiConnect.href = 'https://fonts.googleapis.com';

    const fontsFileConnect = document.createElement('link');
    fontsFileConnect.rel = 'preconnect';
    fontsFileConnect.href = 'https://fonts.gstatic.com';

    const manropeStylesheet = document.createElement('link');
    manropeStylesheet.rel = 'stylesheet';
    manropeStylesheet.href =
      'https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap';

    const css = document.createElement('style');
    const color = window.getComputedStyle(document.body).getPropertyValue('--color-black');
    css.appendChild(
      document.createTextNode(`
        body {
          font-family: Manrope;
          font-size: 13px;
          letter-spacing: 0.325px;
          line-height: 20px;
          margin: 0;
          color: ${color}
        }
      `),
    );

    iframe.contentDocument.head.appendChild(fontsApiConnect);
    iframe.contentDocument.head.appendChild(fontsFileConnect);
    iframe.contentDocument.head.appendChild(manropeStylesheet);
    iframe.contentDocument.head.appendChild(css);

    // Measure after the injected styles apply, then again once late layout
    // (web fonts, images) settles.
    requestAnimationFrame(resizeIframeHeight);
    setTimeout(resizeIframeHeight, 150);

    iframeReady.value = true;
  };
</script>

<template>
  <div class="flex flex-col overflow-hidden min-h-0">
    <template v-if="bodyIncludeStyle">
      <div v-if="!iframeReady" class="text-center">
        <LoadingSpinner class="far" />
      </div>

      <iframe
        v-show="iframeReady"
        v-bind="$attrs"
        :class="
          cn(
            'dark:bg-white dark:text-black prose prose-sm text-foreground wrap-break-word w-full max-w-full leading-5',
            bodyPaddingClass,
            textClassName,
            props.emailEditorClass,
          )
        "
        :srcdoc="emailBody"
        style="border-style: none; height: 0; overflow: hidden"
        @load="resizeIframe"
      />
    </template>

    <div
      v-else
      v-bind="$attrs"
      :dir="textDirection"
      :class="
        cn(
          'dark:bg-white dark:text-black dark:py-2 prose prose-sm text-foreground wrap-break-word focus:outline-hidden max-w-full min-h-8 leading-5 overflow-hidden',
          bodyPaddingClass,
          textClassName,
          props.emailEditorClass,
        )
      "
      v-html="emailBody"
    />

    <div v-if="canToggleQuoted" :class="cn('mt-2.5', quotedPaddingClass)">
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        :aria-label="showQuoted ? 'Hide quoted text' : 'Show quoted text'"
        :title="showQuoted ? 'Hide quoted text' : 'Show quoted text'"
        @click="showQuoted = !showQuoted"
      >
        <i class="fa far fa-ellipsis-h" />
      </Button>
    </div>

    <div
      v-if="nonInlineAttachments.length"
      :id="`${email.id}-attachments`"
      class="flex flex-col w-full gap-4 pt-10 pb-6"
      :class="[bodyPaddingClass, inlineAttachmentClass]"
    >
      <Separator />

      <div class="flex gap-x-2.5 items-center">
        <h3 class="text-xs text-muted-foreground">
          {{ pluralize('attachments', nonInlineAttachments.length, true) }}
        </h3>

        <Separator class="h-5 ml-2" orientation="vertical" />

        <Tooltip v-if="downloadableAttachments.length" :open-delay="0" :close-delay="150">
          <TooltipTrigger as-child>
            <Button
              class="rounded-full"
              type="button"
              variant="ghost"
              size="icon-xs"
              :disabled="isDownloadingAll"
              :aria-label="`Download all ${downloadableAttachments.length} attachments`"
              title="Download all attachments"
              @click="downloadAll"
            >
              <i v-if="isDownloadingAll" class="fa fa-spinner-third animate-spin" />
              <i v-else class="far fa-download text-xs!" />
            </Button>
          </TooltipTrigger>

          <TooltipContent>
            Download

            <template v-if="downloadableAttachments?.length > 1">all</template>

            {{ pluralize('attachment', downloadableAttachments?.length, true) }}
          </TooltipContent>
        </Tooltip>
      </div>

      <div class="flex gap-2.5 flex-wrap w-full" :class="inlineAttachmentClass">
        <AttachmentItem
          v-for="(attachment, idx) in nonInlineAttachments"
          :key="idx"
          :attachment="attachment"
          :email="props.email"
          finalized
        />
      </div>
    </div>
  </div>
</template>
